import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import * as userService from '../../services/userService';
import * as tenantService from '../../services/tenantService';
import * as roleService from '../../services/roleService';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, Menu, MenuItem, Notice, StatusPill } from '../patterns';
import type { DataTableColumn, FilterBarChip } from '../patterns';
import UserEditModal from './UserEditModal';
import UserCreatedModal from './UserCreatedModal';
import ResetPasswordModal from '../auth/ResetPasswordModal';

interface UserRow {
  _id: string;
  username: string;
  email?: string;
  name: string;
  tenantId: string;
  role: any;
  active: boolean;
  createdAt?: string;
}

const SystemUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [createdInfo, setCreatedInfo] = useState<{ name: string; tempPassword: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useBreadcrumb([
    { label: t('nav.system.module'), to: '/system' },
    { label: t('system.users.title') },
  ]);

  const fetchAll = useCallback(async () => {
    try {
      const [u, ts] = await Promise.all([
        userService.listUsers(),
        tenantService.listTenants(),
      ]);
      setUsers(u as UserRow[]);
      setTenants(ts);
      setError(null);
    } catch {
      setError(t('system.users.errors.loadFailed'));
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRolesFor = useCallback(async (tenantId: string) => {
    const all = await roleService.listRoles();
    return (all as any[]).filter(r => r.tenantId === tenantId);
  }, []);

  const filtered = useMemo(() => {
    let rows = users;
    if (tenantFilter !== 'all') rows = rows.filter(u => u.tenantId === tenantFilter);
    if (statusFilter === 'active') rows = rows.filter(u => u.active);
    if (statusFilter === 'inactive') rows = rows.filter(u => !u.active);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [users, tenantFilter, statusFilter, search]);

  const tenantNameFor = (tid: string) => tenants.find(t => t.tenantId === tid)?.name || tid;

  const chips: FilterBarChip[] = [
    { id: 'all', label: t('system.users.filters.allTenants'), active: tenantFilter === 'all' },
    ...tenants.map((tn: any) => ({ id: tn.tenantId, label: tn.name, active: tenantFilter === tn.tenantId })),
    { id: 'status:active',   label: t('system.users.filters.active'),   active: statusFilter === 'active' },
    { id: 'status:inactive', label: t('system.users.filters.inactive'), active: statusFilter === 'inactive' },
  ];

  const handleChipClick = (id: string) => {
    if (id === 'all') {
      setTenantFilter('all');
    } else if (id.startsWith('status:')) {
      const next = id.split(':')[1] as 'active' | 'inactive';
      setStatusFilter(prev => prev === next ? 'all' : next);
    } else {
      setTenantFilter(prev => prev === id ? 'all' : id);
    }
  };

  const columns: DataTableColumn<UserRow>[] = [
    { key: 'name',     label: t('system.users.columns.name'),     sortable: true },
    { key: 'username', label: t('system.users.columns.username'), sortable: true },
    { key: 'email',    label: t('system.users.columns.email'),    render: r => r.email || '—' },
    { key: 'tenant',   label: t('system.users.columns.tenant'),   render: r => tenantNameFor(r.tenantId) },
    { key: 'role',     label: t('system.users.columns.role'),     render: r => r.role?.name || '—' },
    {
      key: 'status',
      label: t('system.users.columns.status'),
      render: r => r.active
        ? <StatusPill variant="ok">{t('system.users.status.active')}</StatusPill>
        : <StatusPill variant="danger">{t('system.users.status.inactive')}</StatusPill>,
    },
    {
      key: 'actions',
      label: t('system.users.columns.actions'),
      render: r => (
        <Menu trigger={<button className="pat-menu__trigger" aria-label={t('system.users.columns.actions')}>⋮</button>} placement="bottom-end">
          <MenuItem onClick={() => { setEditing(r); setShowEditModal(true); }}>
            {t('system.users.actions.edit')}
          </MenuItem>
          <MenuItem onClick={() => setResetTarget(r)}>
            {t('system.users.actions.resetPassword')}
          </MenuItem>
          <MenuItem onClick={() => handleToggleActive(r)}>
            {r.active ? t('system.users.actions.deactivate') : t('system.users.actions.activate')}
          </MenuItem>
        </Menu>
      ),
    },
  ];

  const handleToggleActive = async (r: UserRow) => {
    try {
      await userService.updateUser(r._id, { active: !r.active } as any);
      await fetchAll();
    } catch {
      setError(t('system.users.errors.saveFailed'));
    }
  };

  const handleSave = async (input: any) => {
    if (editing) {
      await userService.updateUser(editing._id, input);
      await fetchAll();
    } else {
      const result = await userService.createUser(input);
      setCreatedInfo({ name: result.user.name, tempPassword: result.tempPassword });
      await fetchAll();
    }
  };

  const tenantCount = useMemo(() => new Set(users.map(u => u.tenantId)).size, [users]);

  return (
    <>
      <PageHeader
        title={t('system.users.title')}
        meta={t('system.users.metaCount', { count: users.length, tenantCount })}
        actions={
          <button className="pat-btn pat-btn--primary" onClick={() => { setEditing(null); setShowEditModal(true); }}>
            {t('system.users.newButton')}
          </button>
        }
      />
      {error && <Notice tone="danger">{error}</Notice>}
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('system.users.searchPlaceholder')}
        chips={chips}
        onChipClick={handleChipClick}
      />
      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={r => r._id}
        onRowClick={r => { setEditing(r); setShowEditModal(true); }}
        emptyState={t('system.users.empty')}
      />

      <UserEditModal
        show={showEditModal}
        user={editing}
        tenants={tenants}
        fetchRolesFor={fetchRolesFor}
        onHide={() => setShowEditModal(false)}
        onSave={handleSave}
      />

      {resetTarget && (
        <ResetPasswordModal
          show={!!resetTarget}
          user={resetTarget}
          onHide={() => setResetTarget(null)}
        />
      )}

      {createdInfo && (
        <UserCreatedModal
          show={!!createdInfo}
          userName={createdInfo.name}
          tempPassword={createdInfo.tempPassword}
          onHide={() => setCreatedInfo(null)}
        />
      )}
    </>
  );
};

export default SystemUsersPage;
